import { useCallback, useEffect, useState } from "react";
import { Grid3x3, Loader2, X, RotateCcw, Users2, Plus, Trash2, Crown } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import { EmployeePicker } from "../../components/os/EmployeePicker";

const AXIS = { 1: "Bajo", 2: "Medio", 3: "Alto" };
const READINESS_LABELS = { listo_ya: "Listo ya", "1_2_anios": "1–2 años", mas_2_anios: "2+ años" };
const CELL_TONE = {
  "3-3": "bg-emerald-500/10 border-emerald-500/30", "3-2": "bg-lime-500/10 border-lime-500/30", "3-1": "bg-amber-500/10 border-amber-500/30",
  "2-3": "bg-lime-500/10 border-lime-500/30", "2-2": "bg-sky-500/10 border-sky-500/30", "2-1": "bg-orange-500/10 border-orange-500/30",
  "1-3": "bg-sky-500/10 border-sky-500/30", "1-2": "bg-orange-500/10 border-orange-500/30", "1-1": "bg-rose-500/10 border-rose-500/30",
};

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className={`bg-white rounded-2xl w-full ${wide ? "max-w-lg" : "max-w-md"} max-h-[90vh] overflow-y-auto p-6`}>
        <div className="flex items-center justify-between mb-4"><h3 className="font-editorial text-2xl">{title}</h3><button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button></div>
        {children}
      </div>
    </div>
  );
}

export default function NineBox() {
  const [tab, setTab] = useState("grid");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [meta, setMeta] = useState(null);

  const load = useCallback(() => { setLoading(true); api.get("/nine-box/grid").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  useEffect(() => { api.get("/nine-box/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <OSLayout title="OMNIA 9-Box">
      <div className="space-y-6" data-testid="ninebox-page">
        <div className="flex gap-2 border-b border-navy/10 pb-2">
          {[["grid", "Matriz 9-box", Grid3x3], ["succession", "Sucesión", Users2]].map(([k, l, Ic]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`nb-tab-${k}`} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm ${tab === k ? "bg-navy text-cream" : "text-navy/55 hover:bg-navy/5"}`}><Ic className="w-4 h-4" /> {l}</button>
          ))}
        </div>

        {tab === "grid" && (loading || !data ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
          <div>
            <p className="text-sm text-navy/50 mb-4">Ejes calculados automáticamente desde evaluaciones y objetivos; toca a una persona para ajustar manualmente.</p>
            <div className="flex gap-2">
              <div className="flex items-center"><span className="text-xs text-navy/40 -rotate-90 whitespace-nowrap">POTENCIAL →</span></div>
              <div className="flex-1">
                <div className="grid grid-cols-3 gap-2">
                  {[3, 2, 1].map((pot) => [1, 2, 3].map((perf) => {
                    const key = `${pot}-${perf}`;
                    const people = data.cells[key] || [];
                    return (
                      <div key={key} className={`rounded-xl border p-2 min-h-[120px] ${CELL_TONE[key]}`} data-testid={`nb-cell-${key}`}>
                        <div className="text-[11px] font-medium text-navy/60 mb-1">{data.box_labels[key]}</div>
                        <div className="space-y-1">
                          {people.map((p) => (
                            <button key={p.user_id} onClick={() => setSel(p)} className="w-full text-left bg-white/80 rounded-lg px-2 py-1 text-xs hover:bg-white transition-colors" data-testid={`nb-person-${p.user_id}`}>
                              <div className="font-medium text-navy truncate">{p.name}</div>
                              <div className="text-navy/40 truncate">{p.puesto || p.area}{p.source === "manual" ? " · ✎" : ""}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  }))}
                </div>
                <div className="grid grid-cols-3 gap-2 mt-1 text-center text-xs text-navy/40"><span>Desempeño Bajo</span><span>Medio</span><span>Alto</span></div>
                <div className="text-center text-xs text-navy/40 mt-1">DESEMPEÑO →</div>
              </div>
            </div>
          </div>
        ))}

        {tab === "succession" && meta && <Succession meta={meta} />}
      </div>
      {sel && <AssessModal key={sel.user_id} person={sel} onClose={() => setSel(null)} onSaved={() => { setSel(null); load(); }} />}
    </OSLayout>
  );
}

function AssessModal({ person, onClose, onSaved }) {
  const [perf, setPerf] = useState(person.performance);
  const [pot, setPot] = useState(person.potential);
  const [note, setNote] = useState(person.note || "");
  const [busy, setBusy] = useState(false);
  const [evo, setEvo] = useState(null);
  useEffect(() => { api.get(`/nine-box/evolution/${person.user_id}`).then((r) => setEvo(r.data)).catch(() => {}); }, [person.user_id]);
  const save = async () => {
    setBusy(true);
    try { await api.post("/nine-box/assess", { user_id: person.user_id, performance: perf, potential: pot, note }); toast.success("Ubicación guardada"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  const reset = async () => { try { await api.delete(`/nine-box/assess/${person.user_id}`); toast.success("Restaurado a automático"); onSaved(); } catch { toast.error("Error"); } };
  return (
    <Modal title={person.name} onClose={onClose}>
      <div className="text-xs text-navy/45 mb-3">Automático: desempeño {AXIS[person.auto_perf]} ({person.perf_pct ?? "—"}%) · potencial {AXIS[person.auto_pot]} ({person.pot_pct ?? "—"}%) · {person.reviews} eval, {person.objectives} obj</div>
      <div className="space-y-4">
        <div><label className="text-xs text-navy/60 mb-1 block">Desempeño</label>
          <div className="flex gap-2">{[1, 2, 3].map((v) => <button key={v} onClick={() => setPerf(v)} className={`btn text-sm flex-1 ${perf === v ? "btn-primary" : "btn-ghost"}`} data-testid={`nb-perf-${v}`}>{AXIS[v]}</button>)}</div></div>
        <div><label className="text-xs text-navy/60 mb-1 block">Potencial</label>
          <div className="flex gap-2">{[1, 2, 3].map((v) => <button key={v} onClick={() => setPot(v)} className={`btn text-sm flex-1 ${pot === v ? "btn-primary" : "btn-ghost"}`} data-testid={`nb-pot-${v}`}>{AXIS[v]}</button>)}</div></div>
        <textarea className="field" rows={2} placeholder="Nota (opcional)" value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      {evo && evo.points.length >= 1 && (
        <div className="mt-4 border-t border-navy/5 pt-3" data-testid="nb-evolution">
          <div className="text-xs text-navy/50 mb-2">Evolución entre periodos</div>
          <div className="flex items-center gap-2 flex-wrap">
            {evo.points.map((pt, i) => (
              <span key={i} className="flex items-center gap-2">
                <span className="text-xs bg-navy/[0.05] rounded-lg px-2 py-1"><span className="text-navy/45">{pt.period}:</span> <span className="font-medium text-navy">{pt.box_label}</span> <span className="text-navy/40">(D{pt.performance}/P{pt.potential})</span></span>
                {i < evo.points.length - 1 && <span className="text-navy/25">→</span>}
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="flex justify-between gap-2 mt-5">
        <button onClick={reset} className="btn btn-ghost text-sm gap-1"><RotateCcw className="w-4 h-4" /> Auto</button>
        <button onClick={save} disabled={busy} className="btn btn-primary" data-testid="nb-assess-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}</button>
      </div>
    </Modal>
  );
}

function Succession({ meta }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { setLoading(true); api.get("/nine-box/succession").then((r) => setPlans(r.data || [])).catch(() => {}).finally(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { try { await api.delete(`/nine-box/succession/${id}`); load(); } catch { toast.error("Error"); } };
  if (loading) return <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  return (
    <div className="space-y-4">
      <button onClick={() => setOpen(true)} className="btn btn-primary gap-2" data-testid="nb-add-succession"><Plus className="w-4 h-4" /> Plan de sucesión</button>
      {plans.length === 0 ? <div className="bg-white border border-navy/10 rounded-2xl p-6 text-sm text-navy/40">Aún no hay planes de sucesión.</div>
        : plans.map((p) => (
          <div key={p.incumbent_id} className="bg-white border border-navy/10 rounded-2xl p-4" data-testid={`nb-plan-${p.incumbent_id}`}>
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium flex items-center gap-2"><Crown className="w-4 h-4 text-gold" /> {p.incumbent_name} <span className="text-xs text-navy/40">{p.puesto}</span></div>
              <button onClick={() => del(p.incumbent_id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {(p.successors || []).map((s, i) => <span key={i} className="text-xs bg-navy/[0.05] rounded-full px-2.5 py-1 text-navy/70">{s.name} · {READINESS_LABELS[s.readiness]}</span>)}
            </div>
          </div>
        ))}
      {open && <SuccessionModal meta={meta} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function SuccessionModal({ meta, onClose, onSaved }) {
  const [incumbent, setIncumbent] = useState(null);
  const [puesto, setPuesto] = useState("");
  const [successors, setSuccessors] = useState([]);
  const [picking, setPicking] = useState(null);
  const [busy, setBusy] = useState(false);
  const addSuccessor = (emp) => { if (emp && !successors.find((s) => s.user_id === emp.id)) setSuccessors([...successors, { user_id: emp.id, name: emp.name, readiness: "1_2_anios" }]); setPicking(null); };
  const save = async () => {
    if (!incumbent) return toast.error("Selecciona al titular del puesto");
    setBusy(true);
    try { await api.post("/nine-box/succession", { incumbent_id: incumbent.id, puesto, successors: successors.map((s) => ({ user_id: s.user_id, readiness: s.readiness })) }); toast.success("Plan guardado"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <Modal title="Plan de sucesión" onClose={onClose} wide>
      <div className="space-y-3">
        <div><label className="text-xs text-navy/60 mb-1 block">Titular del puesto</label><EmployeePicker testid="nb-succ-incumbent" value={incumbent?.id} onChange={setIncumbent} placeholder="¿Quién ocupa el puesto?" /></div>
        <input className="field" placeholder="Puesto" value={puesto} onChange={(e) => setPuesto(e.target.value)} />
        <div>
          <label className="text-xs text-navy/60 mb-1 block">Sucesores</label>
          <div className="space-y-2">
            {successors.map((s, i) => (
              <div key={s.user_id} className="flex items-center gap-2" data-testid={`nb-succ-${i}`}>
                <span className="text-sm flex-1">{s.name}</span>
                <select className="field-sm" value={s.readiness} onChange={(e) => setSuccessors(successors.map((x, j) => j === i ? { ...x, readiness: e.target.value } : x))}>
                  {meta.readiness.map((r) => <option key={r} value={r}>{READINESS_LABELS[r]}</option>)}
                </select>
                <button onClick={() => setSuccessors(successors.filter((_, j) => j !== i))} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <div className="mt-2"><EmployeePicker testid="nb-succ-add" value={picking?.id} onChange={addSuccessor} placeholder="+ Agregar sucesor" /></div>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-5"><button onClick={onClose} className="btn btn-ghost">Cancelar</button><button onClick={save} disabled={busy} className="btn btn-primary" data-testid="nb-succ-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar plan"}</button></div>
    </Modal>
  );
}
